import { Layout } from "@/components/layout"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { ErrorPage } from "@/pages/ErrorPage"
import { AnalyticsEvent, trackEvent } from "@/services/analytics/amplitude"
import { useGetUserFreeTrialsLeftData } from "@/services/api/userService"
import { useEffect, useLayoutEffect, useRef, type ReactNode } from "react"
import { Link, Navigate, Route, Routes, useLocation } from "react-router"
import {
  SettingsMobileNav,
  SettingsNav,
  type SettingsNavGroup,
} from "./components/SettingsNav"
import {
  SettingsLoadError,
  SettingsSkeleton,
} from "./components/SettingsStates"
import { UnsavedChangesProvider } from "./components/UnsavedChanges"
import {
  DEFAULT_SETTINGS_SECTION,
  LEGACY_SETTINGS_HASHES,
  LEGACY_SETTINGS_SEGMENTS,
  SETTINGS_BASE,
  SETTINGS_PATHS,
  SETTINGS_SEGMENTS,
} from "./paths"
import {
  SETTINGS_GROUPS,
  SETTINGS_SECTIONS,
  type SettingsSectionDef,
} from "./sections"
import { useSettingsSetup, type SettingsSetup } from "./setup/useSettingsSetup"
import {
  useSettingsVisibility,
  type SectionVisibility,
} from "./useSettingsVisibility"

/** `/settings`: the page an old `#anchor` link meant; otherwise the setup
 *  checklist while something is left to set up, the default page once
 *  nothing is. Waits for the checks to tell which. */
function SettingsIndexRedirect({
  remaining,
}: {
  remaining: SettingsSetup["remaining"]
}) {
  const { hash } = useLocation()
  const legacy = LEGACY_SETTINGS_HASHES[hash.slice(1)]
  if (legacy) return <Navigate to={SETTINGS_PATHS[legacy]} replace />
  if (remaining === "unknown") return <SettingsSkeleton />
  const section = remaining === "some" ? "setup" : DEFAULT_SETTINGS_SECTION
  return <Navigate to={SETTINGS_PATHS[section]} replace />
}

/** A page this user doesn't get says so, same as any feature-gated route.
 *  One whose check failed offers to run it again. */
function SectionGate({
  visibility,
  onRetry,
  retrying,
  children,
}: {
  visibility: SectionVisibility
  onRetry: () => void
  retrying: boolean
  children: ReactNode
}) {
  if (visibility === "pending") return <SettingsSkeleton />
  if (visibility === "error")
    return <SettingsLoadError onRetry={onRetry} pending={retrying} />
  if (visibility === "hidden")
    return <ErrorPage variant="feature-unavailable" />
  return children
}

function SettingsBreadcrumb({
  group,
  section,
}: {
  group?: SettingsNavGroup
  section?: SettingsSectionDef
}) {
  const { translate } = useTranslation()
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to={SETTINGS_BASE} replace>
              {translate("SETTINGS/TITLE")}
            </Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        {group && section && (
          <>
            {group.labelKey && (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem>{translate(group.labelKey)}</BreadcrumbItem>
              </>
            )}
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{translate(section.titleKey)}</BreadcrumbPage>
            </BreadcrumbItem>
          </>
        )}
      </BreadcrumbList>
    </Breadcrumb>
  )
}

/** `/settings/*`: the settings nav next to the current settings page, inside
 *  the app shell. */
export default function SettingsApp() {
  const visibility = useSettingsVisibility()
  // What is left to set up, marked in the nav.
  const { todoBySection, remaining } = useSettingsSetup(visibility)
  // The one check that can fail is the trial lookup (Trial & usage).
  const trial = useGetUserFreeTrialsLeftData()
  const { pathname } = useLocation()
  const scrollRef = useRef<HTMLDivElement>(null)

  // The page column stays mounted from page to page, so every page (and tab)
  // would otherwise open at the previous one's scroll position.
  useLayoutEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0
  }, [pathname])

  // The checklist leaves the nav once nothing is left on it, but stays at its
  // URL, and in the nav while it is the page shown.
  const showSetup = remaining === "some" || pathname === SETTINGS_PATHS.setup
  const groups: SettingsNavGroup[] = SETTINGS_GROUPS.map((group) => ({
    ...group,
    sections: SETTINGS_SECTIONS.filter(
      (section) =>
        section.group === group.id &&
        visibility[section.id] === "visible" &&
        (section.id !== "setup" || showSetup),
    ),
  })).filter((group) => group.sections.length > 0)

  const section = SETTINGS_SECTIONS.find(
    ({ id }) =>
      pathname === SETTINGS_PATHS[id] ||
      pathname.startsWith(`${SETTINGS_PATHS[id]}/`),
  )
  const group = groups.find(({ id }) => id === section?.group)

  // Each page switch, with the page before it: how many it takes to set
  // things up, and whether the checklist shortens that.
  const previousSection = useRef<string | null>(null)
  const todoCount = Object.values(todoBySection).reduce((sum, n) => sum + n, 0)
  useEffect(() => {
    if (!section) return
    trackEvent(AnalyticsEvent.SETTINGS_PAGE_VIEWED, {
      section: section.id,
      previous_section: previousSection.current,
      setup_remaining: remaining,
      setup_todo: todoCount,
    })
    previousSection.current = section.id
    // Once per page shown, not again as the checks load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section?.id])

  return (
    <Layout
      headerLeftElement={<SettingsBreadcrumb group={group} section={section} />}
    >
      <UnsavedChangesProvider
        pagePath={section ? SETTINGS_PATHS[section.id] : pathname}
      >
        <div data-agent-target="settings.page" className="flex min-h-0 flex-1">
          {/* From lg: below it the app sidebar leaves too little room for two
              columns next to the page. */}
          <aside className="hidden w-64 shrink-0 overflow-y-auto border-r p-4 lg:block">
            <SettingsNav groups={groups} todo={todoBySection} />
          </aside>
          <div ref={scrollRef} className="min-w-0 flex-1 overflow-y-auto">
            <div className="flex w-full flex-col gap-6 px-4 py-6 md:px-10 md:py-10">
              <div className="lg:hidden">
                <SettingsMobileNav groups={groups} currentId={section?.id} />
              </div>
              <Routes>
                <Route
                  index
                  element={<SettingsIndexRedirect remaining={remaining} />}
                />
                {SETTINGS_SECTIONS.map(({ id, Page, hasTabs }) => (
                  <Route
                    key={id}
                    // A page with tabs routes each tab itself.
                    path={`${SETTINGS_SEGMENTS[id]}${hasTabs ? "/*" : ""}`}
                    element={
                      <SectionGate
                        visibility={visibility[id]}
                        onRetry={() => void trial.refetch()}
                        retrying={trial.isFetching}
                      >
                        <Page />
                      </SectionGate>
                    }
                  />
                ))}
                {Object.entries(LEGACY_SETTINGS_SEGMENTS).map(
                  ([segment, id]) => (
                    <Route
                      key={segment}
                      path={segment}
                      element={<Navigate to={SETTINGS_PATHS[id]} replace />}
                    />
                  ),
                )}
                {/* Lands where a bare /settings would. */}
                <Route
                  path="*"
                  element={<Navigate to={SETTINGS_BASE} replace />}
                />
              </Routes>
            </div>
          </div>
        </div>
      </UnsavedChangesProvider>
    </Layout>
  )
}
